//jul17 Login.jsx:
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { auth } from '../firebase';
import {
  setPersistence,
  signInWithEmailAndPassword,
  browserSessionPersistence,
  browserLocalPersistence,
} from 'firebase/auth';

import { useAuth } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';  // Use LoadingContext
import { Box, TextField, Button, FormControlLabel, Checkbox, Paper, Typography, Alert, Divider, Grid } from '@mui/material';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const { isLoading, setLoading } = useLoading(); // Use LoadingContext

  const { currentUser } = useAuth();
  const [pendingRedirect, setPendingRedirect] = useState(false);

  const navigate = useNavigate();
  const location   = useLocation();
  const redirectTo = location.state?.redirectTo || '/';   // fallback = dashboard
  const reason     = location.state?.reason;

  const logoSrc = process.env.PUBLIC_URL + '/utmlogo.svg';

  // Wait for AuthContext to actually report currentUser before navigating.
  // This prevents the "navigate → guard sees user=null → redirect back to /login" flicker,
  // which is what clears your fields momentarily.
  useEffect(() => {
    if (!pendingRedirect) return;

    // Success path: AuthContext is ready with a user -> navigate
    if (currentUser) {
      navigate(redirectTo, { replace: true });
      setPendingRedirect(false);
      setLoading(false, 'Login');
    }

    // IMPORTANT:
    // Do NOT clear loading here when currentUser is falsy.
    // During normal auth hydration, currentUser may be null briefly.
    // Cleanup/unmount + catch() already ensure we don't leave loading stuck.
  }, [pendingRedirect, currentUser, navigate, redirectTo, setLoading]);


  // Safety cleanup: if the Login screen unmounts for any reason, never leave "Login" loading stuck on.
  useEffect(() => {
    return () => {
      setLoading(false, 'Login');
    };
  }, [setLoading]);


  // Removed loading checks related to authLoading and initialization
  // as Index2 already handles these states before rendering this component


  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[Login] handleSubmit: Attempting login with email:", email);

    setLoading(true, 'Login'); // Activate global loading state in LoadingContext

    try {
      setError('');
      const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      console.log("[Login] Setting persistence:", rememberMe ? 'Local' : 'Session');

      await setPersistence(auth, persistenceType);
      console.log("[Login] Persistence set successfully");

      await signInWithEmailAndPassword(auth, email, password);
      console.log("[Login] Sign-in successful");

      // Do NOT navigate immediately.
      // AuthContext may still be hydrating the user/profile which can cause a brief redirect back to /login.
      // Instead, set a pending flag and let the effect navigate once currentUser is ready.
      setPendingRedirect(true);
         

    } catch (error) {
      console.error("[Login] Error signing in:", error.message);
      setError('Incorrect email or password.');
      setLoading(false, 'Login');        // stop spinner on error
      }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" paddingX={2}>
      <Paper elevation={3} sx={{ padding: 3, maxWidth: 400, width: '100%', textAlign: 'center', my: 2 }}>
        <img src={logoSrc} alt="Logo" style={{ maxWidth: '150px', marginTop: '20px', marginBottom: '20px' }} />

        <Typography variant="h5" sx={{ marginBottom: 2 }}>Login to Your Account</Typography>
        {/* Shown only when RequireAccess sent us here */}
        {reason === 'privateResource' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Restricted. Sign in to view the file.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            id="email"
            name="email"
            label="Email"
            type="email"
            required
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}  // Disable input fields during loading
          />
          <TextField
            id="password"
            name="password"
            label="Password"
            type="password"
            required
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}  // Disable input fields during loading
          />
          <FormControlLabel
            control={
              <Checkbox
                id="rememberMe"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            }
            label="Remember Me"
            sx={{ my: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading}  // Disable the login button during loading
            sx={{ mb: 2 }}
          >
            Login
          </Button>
        </form>

        <Divider sx={{ my: 2 }} />
        
        <Grid container justifyContent="space-between">
          <Grid item>
            <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
              <Typography variant="body2">Forgot Password?</Typography>
            </Link>
          </Grid>
          <Grid item>
            <Link to="/signup" style={{ textDecoration: 'none' }}>
              <Typography variant="body2">Don't have an account? Sign up</Typography>
            </Link>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Login;
